"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.AppSourceHistoryCursorError = void 0;
exports.encodeAppSourceHistoryCursor = encodeAppSourceHistoryCursor;
exports.decodeAppSourceHistoryCursor = decodeAppSourceHistoryCursor;
exports.projectAppSourceHistory = projectAppSourceHistory;
/**
 * The history of a source as an App page reads it (`readHistory`), one logic
 * for the platform and the StreamKit: the runs that wrote rows, projected on
 * the rows and columns the page reads now and the cell projection of its page
 * rights. The platform reads its persist runs, the StreamKit the writes of
 * its local server; both answer through this function.
 */
const app_base_contract_1 = require("./app-base-contract");
/** Thrown for a cursor this logic did not hand out. */
class AppSourceHistoryCursorError extends Error {
    constructor() {
        super('App history cursor is invalid');
        this.code = 'APP_BASE_HISTORY_REQUEST_INVALID';
    }
}
exports.AppSourceHistoryCursorError = AppSourceHistoryCursorError;
const toBase64Url = (text) => btoa(String.fromCharCode(...new TextEncoder().encode(text)))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');
const fromBase64Url = (text) => new TextDecoder().decode(Uint8Array.from(atob(text.replace(/-/g, '+').replace(/_/g, '/')), character => character.charCodeAt(0)));
/** The cursor of the runs before the run `id` at `timestamp`. */
function encodeAppSourceHistoryCursor(timestamp, id) {
    return toBase64Url(JSON.stringify([timestamp.toISOString(), id]));
}
/** The run a cursor names; a cursor this logic did not hand out is refused. */
function decodeAppSourceHistoryCursor(cursor) {
    let parsed = null;
    try {
        parsed = JSON.parse(fromBase64Url(cursor));
    }
    catch {
        // An unreadable cursor is refused below.
    }
    const [at, id] = Array.isArray(parsed) ? parsed : [];
    const timestamp = typeof at === 'string' ? new Date(at) : null;
    if (!timestamp ||
        Number.isNaN(timestamp.getTime()) ||
        typeof id !== 'string' ||
        !id)
        throw new AppSourceHistoryCursorError();
    return { timestamp, id };
}
/**
 * A page of the history from the runs of a source, newest first. Each entry
 * names the cells a run wrote with their new value (`values`) and the value
 * before (`previous`); a deleted row names its last state as `previous`. A
 * row the page cannot read names no entry, a column it cannot read no cell;
 * the cells pass the cell projection of the page rights. A deleted row is
 * read by the same right as a living row, its owner (`readVisibleDeleted`).
 * Who wrote a run is a person with access: named only to a page that may
 * read the people (`namesPeople`, members.read), as the member list itself.
 */
async function projectAppSourceHistory(input) {
    const written = input.runs.flatMap(run => run.rows.map(row => ({ run, row })));
    const living = new Set();
    const deleted = new Set();
    const readable = new Set();
    const readIn = async (read, ids, into) => {
        // One exact-ID read of the rows of the page, in blocks of one read.
        for (let start = 0; start < ids.length; start += app_base_contract_1.APP_BASE_BUDGETS.maxDatasetRows) {
            const seen = await read(ids.slice(start, start + app_base_contract_1.APP_BASE_BUDGETS.maxDatasetRows));
            seen.rowIds.forEach(id => into.add(id));
            seen.columnIds.forEach(id => readable.add(id));
        }
    };
    const idsOf = (wantDeleted) => [
        ...new Set(written
            .filter(entry => 'deleted' in entry.row === wantDeleted)
            .map(entry => entry.row.rowId)),
    ];
    await readIn(input.readVisible, idsOf(false), living);
    await readIn(input.readVisibleDeleted, idsOf(true), deleted);
    const readableCells = (cells) => cells
        ? Object.fromEntries(Object.entries(cells)
            .filter(([columnId]) => readable.has(columnId))
            .map(([columnId, value]) => [columnId, { value }]))
        : null;
    const shown = written.flatMap(({ run, row }) => {
        const gone = 'deleted' in row;
        if (!(gone ? deleted : living).has(row.rowId))
            return [];
        const cells = gone ? {} : readableCells(row.cells);
        const before = readableCells(row.before);
        const names = Object.keys(gone ? (before ?? {}) : cells);
        return names.length
            ? [{ run, rowId: row.rowId, gone, names, cells, before }]
            : [];
    });
    // The written values and the values before pass the same projection.
    const project = async (rows) => {
        const projected = await input.projectCells(rows);
        if (projected.length !== rows.length)
            throw new Error('App history projection changed the rows');
        return projected;
    };
    const values = await project(shown.map(entry => ({ id: entry.rowId, cells: entry.cells })));
    const previous = await project(shown.map(entry => ({ id: entry.rowId, cells: entry.before ?? {} })));
    const kept = (cells, names) => Object.fromEntries(names
        .filter(columnId => columnId in cells)
        .map(columnId => [columnId, cells[columnId].value]));
    const entries = shown.flatMap((entry, index) => {
        const after = kept(values[index].cells, entry.names);
        const prior = kept(previous[index].cells, entry.names);
        const changedColumnIds = entry.names.filter(columnId => entry.gone ? columnId in prior : columnId in after);
        return changedColumnIds.length
            ? [
                {
                    rowId: entry.rowId,
                    at: entry.run.timestamp.toISOString(),
                    actorId: input.namesPeople ? entry.run.editorId : null,
                    changedColumnIds,
                    values: after,
                    previous: entry.before ? prior : null,
                    ...(entry.gone ? { deleted: true } : {}),
                },
            ]
            : [];
    });
    const last = input.runs.at(-1);
    return {
        entries,
        ...(input.more && last
            ? { nextCursor: encodeAppSourceHistoryCursor(last.timestamp, last.id) }
            : {}),
    };
}
